// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Button, Flex, Image, Loading, Typography } from '@iheartjane/reefer';
import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { Layout } from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useGetKiosk } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useThemeGenerator,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  KioskThemeProvider,
  useComplianceLanguage,
  useKioskJaneModeContext,
} from '@jane/kiosk/providers';
import {
  byMedical,
  capitalizeEveryWord,
  ensureOneMedicalAndRecreationalStore,
  trackLandingTap,
} from '@jane/kiosk/util';
import { useGetStore } from '@jane/shared/data-access';
import type { BaseStore, Store } from '@jane/shared/types';

const StyledRoundedButton = styled(Button)({ borderRadius: '64px' });

/** @see https://png-pixel.com/ */
const transparentPixel =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

type KioskButtonProps = {
  complianceLanguageSettings: Store['store_compliance_language_settings'];
  kioskStores: BaseStore[];
  logo: Store['kiosk_photo'];
};

const KioskButtons = ({ kioskStores, logo }: KioskButtonProps) => {
  const navigate = useNavigate();
  const { isIpad, getKioskSpacing } = useKioskSpacing();
  const isJaneMode = useKioskJaneModeContext();
  const { getWord } = useComplianceLanguage();

  const isSingleStore = kioskStores.length === 1;

  const getButtonLabel = useCallback(
    (recreational: boolean) =>
      capitalizeEveryWord(
        isSingleStore
          ? 'Shop Now'
          : `Shop ${recreational ? (getWord('recreational') ?? 'Rec') : 'Med'}`
      ),
    [getWord, isSingleStore]
  );

  const onKioskButtonClick = useCallback(
    (kioskStore: BaseStore) => {
      trackLandingTap({
        cta: [getButtonLabel(kioskStore.recreational)],
        isJaneMode,
        logoDisplayed: !!logo,
        store: kioskStore,
      });

      navigate(`/stores/${kioskStore.id}/menu`);
    },
    [getButtonLabel, isJaneMode, navigate, logo]
  );

  return (
    <Flex>
      {kioskStores.sort(byMedical).map((kioskStore) => {
        const { id, recreational } = kioskStore;
        const label = getButtonLabel(recreational);

        return (
          <StyledRoundedButton
            mr={isSingleStore ? undefined : getKioskSpacing(24, 8)}
            ml={isSingleStore ? undefined : getKioskSpacing(24, 8)}
            size={isIpad ? 'large' : 'jumbo'}
            variant="secondary"
            onClick={() => onKioskButtonClick(kioskStore)}
            key={id}
            label={
              <Typography
                color="text-inverse"
                mr={16}
                ml={16}
                variant={isIpad ? 'body-semibold' : 'h2-semibold'}
              >
                {label}
              </Typography>
            }
          />
        );
      })}
    </Flex>
  );
};

const LandingWrapper = ({ children }: PropsWithChildren) => {
  const isJaneMode = useKioskJaneModeContext();
  const whiteTheme = useThemeGenerator('#FFFFFF');
  if (isJaneMode) {
    return (
      <KioskThemeProvider theme={whiteTheme} themeId="landing-white-theme">
        <div id="landing-white-theme">{children}</div>
      </KioskThemeProvider>
    );
  }
  return <div>{children}</div>;
};

export const Landing = () => {
  const { isIpad, getKioskSpacing } = useKioskSpacing();
  const imgDimension = isIpad ? '480px' : '720px';
  const {
    kiosk: initialKiosk,
    kioskId,
    store: initialStore,
    storeId,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const {
    data: store,
    isSuccess: isStoreSuccess,
    isPending,
  } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: kiosk, isSuccess: isKioskSuccess } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });
  const isLoaded = isKioskSuccess && isStoreSuccess;
  const filteredKioskStores = ensureOneMedicalAndRecreationalStore(
    kiosk?.stores
  );

  if (kiosk?.skip_landing_page) {
    return <Navigate to={`/stores/${storeId}/menu`} />;
  }

  return (
    <LandingWrapper>
      <Layout isFullScreen>
        {isPending && <Loading color="black" size="lg" />}
        {isLoaded && (
          <>
            {store.kiosk_photo && (
              <Image
                altText="store logo"
                fallbackSrc={transparentPixel}
                objectFit="contain"
                objectPosition="center bottom"
                src={store.kiosk_photo}
                height={imgDimension}
                width={imgDimension}
                responsive
              />
            )}
            <Typography
              color="text-info"
              mt={24}
              mb={getKioskSpacing(64, 32)}
              textAlign="center"
              variant={isIpad ? 'h2-semibold' : 'h1-semibold'}
            >
              {kiosk?.name || store.name}
            </Typography>
            {filteredKioskStores.length > 0 && (
              <KioskButtons
                complianceLanguageSettings={
                  store.store_compliance_language_settings
                }
                kioskStores={filteredKioskStores}
                logo={store.kiosk_photo}
              />
            )}
          </>
        )}
      </Layout>
    </LandingWrapper>
  );
};
